/**
 * BaseElement for all the other components that will be created
 *
 * This component will host shared properties and methods between all the components
 *
 * baseCss includes all basic CSS styles that need to be shared with all components that implement this class
 * example:
 *  static override styles = [
 *    BaseComponent.styles,
 *    css`local-component CSS`
 *  ] as CSSResultGroup
 */

import { property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { baseCss } from './base-css.js'

export class BaseComponent extends LitElement {
  protected readonly baseCSSClass: string = 'il-component'
  protected extraCSSClasses: string = ''

  @property({ attribute: 'is-loading', type: Boolean })
  isLoading: boolean = false

  @property({attribute: 'animation-curve', type: String})
  animationCurve?: string
  
  @property({attribute: 'fast-animation', type: Boolean})
  fastAnimation: boolean = false

  // change the value of this flag whenever you need to perform DOM operations after the component
  // has been rendered, useful when performing alterations to getters on updated() call
  // also useful as a lock, when you need to perform operations a single time at DOM render, then never again
  @state() DOMReady: boolean = false

  static events: { [key: string]: string } = {}

  // baseCss is already a CSSResult - wrapping it in css`${baseCss}` is what a minifier in
  // the customer-flows build pipeline strips to empty, silently dropping every shared style
  static override styles: CSSResultGroup = baseCss

  /**
   * useful when needing to render a generic loading spinner over any Iliad component
   * each component needs to provide custom CSS for this to work correctly
   * can be overridden by specific components whenever you need a specific loading state
   * @protected
   */
  protected renderLoadingState (): TemplateResult {
    return this.isLoading
      ? html`
        <div class="il-component__loading-container" part="loading-container">
          <slot name="loading-spinnaer">
            <il-loader></il-loader>
          </slot>
        </div>`
      : html``
  }
}
